/**
 * Debug Utility for Custom Claims Verification
 *
 * This utility helps verify whether the JWT claims are set correctly.
 * It can be called from the browser console.
 *
 * Usage (in browser console):
 *   window.checkClaimsStatus()
 *   window.checkClaimsStatus(true)  // verbose mode
 *
 * JWT Claims Format (Minimal - ~50-80 bytes):
 *   - 'a': admin (boolean) - quick admin check
 *   - 'cv': claimsVersion (number) - for token refresh detection
 *   - 'ek': encoded key (string) - email with '.' replaced by ',' for RTDB lookups
 *
 * Permissions are now looked up from:
 *   - Firestore: /users/{email}.pageAccess (for Firestore rules)
 *   - RTDB: /permissions/{env}/{encodedEmail} (for RTDB rules)
 *
 * Legacy format (being phased out):
 *   - 'pa': pageAccess (compressed format) - may still exist on old tokens
 */

import { getAuth, getIdTokenResult } from 'firebase/auth';
import { getApps, getApp } from 'firebase/app';

interface ClaimsStatus {
  isLoggedIn: boolean;
  usingMinimalClaims: boolean;
  hasLegacyClaims: boolean;
  claimsVersion: number | null;
  isAdmin: boolean;
  encodedKey: string | null;
  legacyPageCount: number;
  claimsSizeEstimate: number;
  rawClaims: Record<string, unknown> | null;
}

/**
 * Check the current status of custom claims.
 *
 * @param verbose - If true, logs the full claims object
 * @returns ClaimsStatus object with details
 */
export async function checkClaimsStatus(verbose = false): Promise<ClaimsStatus> {
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🔍 Checking Custom Claims Status...');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  // Check if Firebase is initialized
  if (getApps().length === 0) {
    console.log('❌ Firebase not initialized');
    return {
      isLoggedIn: false,
      usingMinimalClaims: false,
      hasLegacyClaims: false,
      claimsVersion: null,
      isAdmin: false,
      encodedKey: null,
      legacyPageCount: 0,
      claimsSizeEstimate: 0,
      rawClaims: null,
    };
  }

  const auth = getAuth(getApp());
  const user = auth.currentUser;

  if (!user) {
    console.log('❌ Not logged in - no Firebase Auth user');
    return {
      isLoggedIn: false,
      usingMinimalClaims: false,
      hasLegacyClaims: false,
      claimsVersion: null,
      isAdmin: false,
      encodedKey: null,
      legacyPageCount: 0,
      claimsSizeEstimate: 0,
      rawClaims: null,
    };
  }

  console.log(`👤 User: ${user.email || user.uid}`);

  // Get token with claims
  const tokenResult = await getIdTokenResult(user);
  const claims = tokenResult.claims;

  // Check for minimal format (new) - uses 'ek' for encoded key
  const encodedKey = claims.ek as string | undefined;
  const hasMinimalClaims = encodedKey != null;

  // Check for legacy format - uses 'pa' for pageAccess
  const legacyPA = claims.pa as Record<string, unknown> | undefined;
  const hasLegacyClaims = legacyPA != null;

  // Get claims version
  const claimsVersion = (claims.cv as number) || (claims.claimsVersion as number) || null;
  const isAdmin = claims.a === true || claims.admin === true;

  // Estimate claims size
  const customClaims = { a: claims.a, cv: claims.cv, ek: claims.ek, pa: claims.pa };
  const claimsSizeEstimate = JSON.stringify(customClaims).length;

  // Determine status
  if (hasMinimalClaims && !hasLegacyClaims) {
    console.log('✅ Using MINIMAL JWT claims (NEW - permissions from Firestore/RTDB)');
    console.log('   → Security rules look up permissions from database');
  } else if (hasMinimalClaims && hasLegacyClaims) {
    console.log('⚠️  TRANSITIONAL: Has both minimal (ek) AND legacy (pa) claims');
    console.log('   → This is expected during migration period');
  } else if (hasLegacyClaims) {
    console.log('⚠️  Using LEGACY JWT claims (pa format)');
    console.log('   → Log out and back in to get minimal claims');
  } else {
    console.log('❌ No custom claims found');
    console.log('   → Log out and back in to get claims set');
  }

  console.log('');
  console.log('📊 Claims Details:');
  console.log(`   • claimsVersion (cv): ${claimsVersion ?? 'not set'}`);
  console.log(`   • admin (a): ${isAdmin}`);
  console.log(`   • encodedKey (ek): ${encodedKey ?? 'not set'}`);
  console.log(`   • Estimated size: ${claimsSizeEstimate} bytes (limit: 1000)`);

  const legacyPageCount = legacyPA ? Object.keys(legacyPA).length : 0;
  if (hasLegacyClaims) {
    console.log(`   • Legacy pages in claims (pa): ${legacyPageCount}`);
    if (verbose) {
      console.log('');
      console.log('📄 Legacy pageAccess (pa):');
      for (const [page, access] of Object.entries(legacyPA || {})) {
        if (typeof access === 'object' && access !== null) {
          console.log(`   • ${page}: ${JSON.stringify(access)}`);
        } else {
          console.log(`   • ${page}: ${access}`);
        }
      }
    }
  }

  console.log('');
  console.log('📝 Permission Lookup:');
  if (hasMinimalClaims || !hasLegacyClaims) {
    console.log('   • Firestore rules read from: /users/{email}.pageAccess');
    console.log(`   • RTDB rules read from: /permissions/{env}/${encodedKey ?? '<ek>'}`);
  }
  if (hasLegacyClaims) {
    console.log('   • Legacy rules read from: JWT claims (pa)');
  }

  if (verbose) {
    console.log('');
    console.log('📝 Full Claims Object:');
    console.log(claims);
  }

  console.log('');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  return {
    isLoggedIn: true,
    usingMinimalClaims: hasMinimalClaims && !hasLegacyClaims,
    hasLegacyClaims,
    claimsVersion,
    isAdmin,
    encodedKey: encodedKey ?? null,
    legacyPageCount,
    claimsSizeEstimate,
    rawClaims: verbose ? (claims as Record<string, unknown>) : null,
  };
}

/**
 * Force refresh the ID token to get latest claims from Firebase Auth.
 * Use this after an admin updates your permissions.
 */
export async function forceRefreshToken(): Promise<void> {
  console.log('🔄 Force refreshing ID token...');

  if (getApps().length === 0) {
    console.log('❌ Firebase not initialized');
    return;
  }

  const auth = getAuth(getApp());
  const user = auth.currentUser;

  if (!user) {
    console.log('❌ Not logged in');
    return;
  }

  await user.getIdToken(true);
  console.log('✅ Token refreshed! Run checkClaimsStatus() to verify.');
}

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).checkClaimsStatus = checkClaimsStatus;
  (window as unknown as Record<string, unknown>).forceRefreshToken = forceRefreshToken;
}
